import {
  AfterViewInit,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  inject,
  ChangeDetectionStrategy
} from '@angular/core';
import moment from 'moment';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { CommentType } from '@coin/shared/core/util-enums';
import { SimplebarAngularComponent, SimplebarAngularModule } from 'simplebar-angular';
import { CoinUser, Comment, Employee, LetterComment, Todo } from '@coin/shared/core/util-models';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CdkDrag, CdkDragHandle } from '@angular/cdk/drag-drop';
import { MatIcon } from '@angular/material/icon';
import { CommentComponent } from '../comment/comment.component';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { V2TextInputComponent } from '@coin/shared/core/ui-storybook';

interface PostLetterCommentService {
  postComment: (letterId: string, comment: LetterComment) => Observable<Comment>;
}
interface PostSeasonRecordCommentService {
  postComment: (recordId: string, comment: Comment) => Observable<Comment>;
}
interface PostMeritRecordCommentService {
  postComment: (seasonId: string, employeeId: string, message: string, reason?: string) => Observable<Comment>;
}

@Component({
  selector: 'coin-siemens-energy-comments',
  templateUrl: './comments.component.html',
  styleUrls: ['./comments.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CdkDrag,
    MatIcon,
    CdkDragHandle,
    SimplebarAngularModule,
    CommentComponent,
    V2TextInputComponent,
    FormsModule,
    TranslatePipe
  ]
})
export class CommentsComponent implements OnInit, AfterViewInit {
  private readonly destroyRef = inject(DestroyRef);

  @Input() comments: Array<LetterComment | Comment>;
  @Input() commentService: PostLetterCommentService | PostSeasonRecordCommentService | PostMeritRecordCommentService;
  @Input() todo: Todo; // optional
  @Input() letterId?: string;
  @Input() user: CoinUser;
  @Input() employee?: Employee & { firstName?: string; lastName?: string };
  @Input() employeeId?: string;
  @Input() embedded: boolean;
  @Input() inDialog: boolean;
  @Input() additionalId: string;
  @Input() commentType: CommentType;
  /** Hides the input row, so existing comments stay readable without allowing a new one. */
  @Input() isReadonly: boolean;
  @Output() closeMe = new EventEmitter<void>();
  @Output() add = new EventEmitter<string>();

  @ViewChild('simpleBar') simpleBar: SimplebarAngularComponent;
  public commentText = '';

  get employeeName(): string {
    return this.employee
      ? `(${this.employee.firstname || this.employee.firstName || ''} ${this.employee.lastname || this.employee.lastName || ''})`
      : '';
  }

  ngOnInit(): void {
    this.comments = this.getSortedComments();
  }

  ngAfterViewInit(): void {
    this.scrollToBottom();
  }

  public addComment(): void {
    if (this.commentText?.trim()) {
      switch (this.commentType) {
        case CommentType.SeasonRecord:
          this.addSeasonRecordComment();
          break;
        case CommentType.MeritRecord: {
          if (this.letterId) {
            this.addLetterComment();
          } else {
            this.addMeritRecordComment();
          }
          break;
        }
        case CommentType.Letter:
          this.addLetterComment();
          break;
        default:
          this.addGenericComment();
      }
    }
  }

  public addLetterComment(): void {
    const comment = new LetterComment(
      this.createCommentObject({
        linkedTodoId: this.todo?.id
      })
    );

    (this.commentService as PostLetterCommentService)
      .postComment(this.letterId, comment)
      .pipe(
        tap(() => this.addCommentLocally(comment)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  public addSeasonRecordComment(): void {
    const comment = this.createCommentObject();

    (this.commentService as PostSeasonRecordCommentService)
      .postComment(this.additionalId || this.letterId, comment)
      .pipe(
        tap(() => this.addCommentLocally(comment)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  public addMeritRecordComment(): void {
    const comment = this.createCommentObject();

    (this.commentService as PostMeritRecordCommentService)
      .postComment(this.additionalId, this.employee?.employeeId || this.employeeId, this.commentText)
      .pipe(
        tap(() => this.addCommentLocally(comment)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  public addGenericComment(): void {
    this.add.emit(this.commentText);
    this.addCommentLocally(this.createCommentObject());
  }

  private createCommentObject(additional?: Record<string, unknown>) {
    return {
      text: this.commentText,
      author: {
        firstname: this.user.firstname,
        lastname: this.user.lastname,
        gid: this.user.gid,
        id: this.user.id
      },
      createdAt: moment().utc().toISOString(),
      ...additional
    };
  }

  private addCommentLocally(comment: Comment | LetterComment): void {
    this.comments.push(comment);
    this.scrollToBottom();
    this.commentText = '';
  }

  public scrollToBottom(): void {
    if (this.simpleBar) {
      setTimeout(() => {
        this.simpleBar?.SimpleBar?.getScrollElement().scrollTo({ top: 50000, behavior: 'smooth' });
      }, 400);
    }
  }

  private getSortedComments(): Array<LetterComment | Comment> {
    if (this.comments?.length) {
      return (this.comments as Array<LetterComment & Comment>).sort(
        (a, b) => moment(a.timestamp || a.createdAt).valueOf() - moment(b.timestamp || b.createdAt).valueOf()
      );
    }
    return this.comments;
  }

  public close(): void {
    this.closeMe.emit();
  }
}
